import React, { } from "react";
import { useQuery } from "@apollo/client";
import { colors, fonts, glyphs, mediaQueriesNumbers, useMediaQuery } from "../../Styles";
import { NewSelect, PrimaryPopover, WaitAnimation } from "..";
import { NewFieldStyles, SelectFieldOption_Values } from "../../Definitions";
import { useUser } from "../../Services";
import { DomainLanControllersResponse, GET_LAN_CONTROLLERS_BY_DOMAIN } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLanControllersByDomain";
import { useAicoConnection } from "./_ToolsComponents/aicoConnectionContext";

export interface AicoConnectionProps {
	hideLANControllers?: boolean
	inTable?: boolean
}

export default function AicoConnection({hideLANControllers, inTable=false}: AicoConnectionProps) {
	const { userFarms } = useUser();
	const { state, setDomain, setLanControllerId } = useAicoConnection();
	const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`); 
	const mobile = useMediaQuery(`(max-width: ${mediaQueriesNumbers.laptopMax}px)`); 

	const { data: lanControllers, loading, error } = useQuery<DomainLanControllersResponse>(GET_LAN_CONTROLLERS_BY_DOMAIN, { //TODO(MTH): handle fetching more lan controllers
		variables: { domain: state.domain, limit: 500, offset: 0 },
		skip: !state.domain	
	});

	function handleDomainChange(value: SelectFieldOption_Values | undefined) {
		setDomain(value?.value ?? null);
	}

	function handleLanControllerChange(value: SelectFieldOption_Values | undefined) {
		if (!value?.value) {
			return;
		}

		const lanControllerId = parseInt(value?.value);

		if (isNaN(lanControllerId)) {
			return;
		}

		setLanControllerId(lanControllerId);
	}

	function getLCSelectState() {
		const lc = lanControllers?.controller.lanControllers.find(e => e.lan_controller_id === state.lanControllerId);
		if (!lc) {
			return undefined;
		}

		return {
			label: String(lc.lan_controller_id) + " | " + lc.cpu_id, 
			value: String(lc.lan_controller_id),
			id: lc.lan_controller_id
		}
	}

	const _inTableStyles = inTable === false ? NewFieldStyles.greyDarkPrimary : mobileXs ? NewFieldStyles.greyDarkSecondary : NewFieldStyles.greySecondary
	const _inTableWidth = inTable === false ? "230px" : mobile ? "150px" : undefined

	const connectionFields = (
		<div style={{ display: "flex", alignItems: "center", flexDirection: mobileXs ? "column" : undefined, gap: "5px", position: "relative" }}>
			<NewSelect
				placeholder="Domain"
				fieldOverrides={{
					fieldStyle: _inTableStyles,
					fieldWidth: _inTableWidth
				}}
				options={userFarms?.map(farm => ({label: farm.domain, value: farm.domain})) ?? []}
				isClearable
				isSearchable
				onChange={handleDomainChange}
				valueOverrides={{
					defaultValue: state.domain ? { label: state.domain, value: state.domain, id: state.domain } : undefined
				}}
			/>

			{!hideLANControllers &&
			<>
			{loading && <WaitAnimation/>}
			{error && <div style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, width: mobile ? "150px" : "280px", }}>Cannot fetch LAN Controllers</div>}
			{!loading && !error && lanControllers?.controller.lanControllers &&
				<NewSelect
					options={lanControllers?.controller.lanControllers.map(lc => ({label: String(lc.lan_controller_id) + " | " + lc.cpu_id, value: String(lc.lan_controller_id)})) ?? []}
					isClearable
					isSearchable
					placeholder="Lan Controllers"
					fieldOverrides={{
						fieldStyle: _inTableStyles,
						fieldWidth: _inTableWidth
					}}
					onChange={handleLanControllerChange}
					valueOverrides={{
						defaultValue: getLCSelectState()
					}}
				/>
			}
			</>
			}
		</div>
	)

	return (<>
		{mobileXs ? (
			<div style={{position: "relative"}}>
				<PrimaryPopover
					contentFromTableButton
					content={"connection"}
					popoverContent={connectionFields}
					popoverHeader={""}
					overrides={{
						popoverBgColor: colors.greyDarkPrimary,
						btnStartEnhancer: glyphs.Setting({})
					}}
				/>
			</div>
		) : (
			connectionFields
		)}
	</>);
}
